import { createContext, useContext, useEffect, useState } from "react";

export const STRINGS = {
  en: {
    brand: { first: "Charbel", last: "Moubarak" },
    nav: { about: "About", properties: "Properties", reviews: "Reviews", gallery: "Gallery", video: "Video", faq: "FAQ", contact: "Contact" },
    hero: {
      sub1: "Luxury Real Estate Specialist",
      sub2: "International Markets",
      stats: [
        { value: "8+", label: "Years Experience" },
        { value: "200+", label: "Clients Served" },
        { value: "750M+", label: "AED Property Value" },
      ],
      scrollLabel: "Scroll to About",
    },
    marquee: ["Luxury Off-Plan", "International Markets", "Dubai", "750M+ AED Transacted", "200+ Clients", "Turkey · Lebanon · China"],
    about: {
      index: "01",
      overline: "About",
      title: "The Man Behind the Deals",
      quote: "Luxury is not a price point. It is a standard of care.",
      p1: "For eight years, Charbel Moubarak has guided investors and families through the world's most competitive property markets — Dubai, Turkey, Lebanon, China and beyond. His focus is singular: off-plan residential and commercial assets that reward early conviction.",
      p2: "With more than 750 million AED in successful transactions and 200+ clients who rate him five stars on Google, Charbel pairs deep market intelligence with something rarer — genuinely personal service. Every inquiry is answered by Charbel himself.",
      badge: "Dubai · Est. 2018",
      chapters: [
        { num: "01", title: "A Global Footprint", text: "From Dubai's towers to Istanbul, Beirut and Shanghai — eight years closing landmark deals across four continents." },
        { num: "02", title: "Off-Plan Authority", text: "Early access to the region's most anticipated launches, with entry prices and payment plans reserved for his private client list." },
        { num: "03", title: "Clients First, Always", text: "200+ clients, a perfect 5-star Google rating, and 750 million AED transacted — every deal guided personally, end to end." },
      ],
    },
    properties: { index: "02", overline: "Portfolio", title: "Featured Off-Plan Properties", viewDetails: "View Details", handover: "Handover", payment: "Payment Plan", visa: "Golden Visa Eligible", offplan: "Off-Plan", filters: { all: "All", sobha: "Sobha", damac: "DAMAC", binghatti: "Binghatti", omniyat: "Omniyat" } },
    reviews: { index: "03", overline: "Reviews", title: "What Clients Say", googleLeft: "Rated", googleRight: "on Google Reviews" },
    gallery: { index: "04", overline: "Gallery", title: "A Life in Prime Real Estate" },
    video: {
      index: "04",
      overline: "The Series",
      title: "The Sales Drive",
      kicker: "Investment Opportunities · Dubai & Beyond",
      desc: "The first real estate series of its kind launched in Dubai — unfiltered conversations with the master developers behind the city's most ambitious projects, hosted by Charbel Moubarak.",
      play: "Play episode",
      allEps: "All Episodes — The Sales Drive",
      tagline: "Dubai Real Estate, Unfiltered.",
      subscribe: "Subscribe on YouTube",
    },
    ig: { overline: "Instagram", title: "Latest on Instagram" },
    project: { back: "Back to Portfolio", highlights: "Project Highlights", from: "Price Range", notFound: "Project not found", enquire: "Enquire on WhatsApp", book: "Book a Meeting", type: "Type" },
    faq: {
      index: "05",
      overline: "FAQ",
      title: "Buying in Dubai, Answered",
      items: [
        {
          q: "Can foreigners buy property in Dubai?",
          a: "Yes. Foreign nationals can buy with 100% ownership in Dubai's freehold zones — including Downtown Dubai, Dubai Marina, Palm Jumeirah and Business Bay. Charbel handles the entire process, remotely or in person.",
        },
        {
          q: "Is it safe to buy off-plan property in Dubai?",
          a: "Dubai regulates off-plan sales through RERA-approved escrow accounts — your payments are tied to construction milestones, not the developer's pocket. Charbel lists only vetted developers: Emaar, DAMAC, Sobha, Binghatti and Omniyat.",
        },
        {
          q: "How much do I need to start investing in Dubai real estate?",
          a: "Entry points start around AED 1M, with payment plans from just 10% down — for example DAMAC Islands' 75/25 plan. Flexible construction-linked plans are available across the portfolio.",
        },
        {
          q: "Does buying property in Dubai qualify me for a Golden Visa?",
          a: "Yes — property investments of AED 2M or more qualify for the 10-year UAE Golden Visa. Every project in Charbel's portfolio is Golden Visa eligible, and he guides clients through the application.",
        },
        {
          q: "Why buy off-plan with Charbel Moubarak?",
          a: "8 years of international experience, 200+ clients, 750M+ AED transacted and a 5-star Google rating — plus launch-day access and payment plans reserved for his private client list across Dubai, Turkey, Lebanon and China.",
        },
      ],
    },
    contact: {
      index: "06",
      overline: "Contact",
      title: "Begin the Conversation",
      promise: "Every inquiry is answered personally by Charbel — never an assistant, never a call centre.",
      methods: [
        { title: "WhatsApp", note: "Quick response, usually within the hour", cta: "WhatsApp for Quick Response" },
        { title: "Call Directly", note: "+971 54 771 4890", cta: "Call Now" },
        { title: "Private Consultation", note: "30-minute portfolio review, online or in Dubai", cta: "Book a Meeting" },
        { title: "Instagram", note: "@moubarak.charbel", cta: "Follow the Journey" },
      ],
      form: {
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        interest: "Property Interest",
        interests: ["Commercial", "Residential", "Other"],
        message: "Tell me about your investment goals…",
        submit: "Send Inquiry",
        sending: "Sending…",
      },
      errors: {
        name: "Please enter your full name.",
        email: "Please enter a valid email address.",
        phone: "Please enter your phone number.",
        interest: "Please select a property interest.",
        message: "Please write a short message.",
      },
      success: "Inquiry sent. Charbel will reach out within 24 hours.",
      failure: "Something went wrong — please use WhatsApp for an instant response.",
    },
    footer: { tagline: "Dubai | International Markets | Luxury Off-Plan Specialist", rights: "All rights reserved." },
  },
  ar: {
    brand: { first: "شاربل", last: "مبارك" },
    nav: { about: "عنّي", properties: "العقارات", reviews: "آراء العملاء", gallery: "المعرض", video: "الفيديو", faq: "الأسئلة", contact: "تواصل" },
    hero: {
      sub1: "أخصائي العقارات الفاخرة",
      sub2: "الأسواق الدولية",
      stats: [
        { value: "+8", label: "سنوات من الخبرة" },
        { value: "+200", label: "عميل سعيد" },
        { value: "+750 مليون", label: "درهم قيمة الصفقات" },
      ],
      scrollLabel: "انتقل إلى عنّي",
    },
    marquee: ["عقارات فاخرة على المخطط", "أسواق دولية", "دبي", "صفقات تتجاوز 750 مليون درهم", "أكثر من 200 عميل", "تركيا · لبنان · الصين"],
    about: {
      index: "٠١",
      overline: "عنّي",
      title: "الرجل خلف الصفقات",
      quote: "الفخامة ليست سعراً على الإطلاق — بل معيارٌ في الاهتمام.",
      p1: "على مدى ثماني سنوات، رافق شاربل مبارك المستثمرين والعائلات في أكثر أسواق العقارات تنافسية في العالم — دبي وتركيا ولبنان والصين وما بعدها. تركيزه واحد: العقارات السكنية والتجارية على المخطط التي تكافئ القرار المبكر.",
      p2: "مع أكثر من 750 مليون درهم في صفقات ناجحة وأكثر من 200 عميل يمنحونه خمس نجوم على جوجل، يجمع شاربل بين معرفة عميقة بالسوق وما هو أندر — خدمة شخصية حقيقية. كل استفسار يجيب عليه شاربل بنفسه.",
      badge: "دبي · منذ 2018",
      chapters: [
        { num: "٠١", title: "حضور عالمي", text: "من أبراج دبي إلى إسطنبول وبيروت وشنغهاي — ثماني سنوات من إبرام صفقات فارقة عبر أربع قارات." },
        { num: "٠٢", title: "خبير العقارات على المخطط", text: "وصول مبكر إلى أكثر الإطلاقات المنتظرة في المنطقة، بأسعار دخول وخطط دفع مخصصة لقائمة عملائه الخاصة." },
        { num: "٠٣", title: "العميل أولاً، دائماً", text: "أكثر من 200 عميل، وتقييم خمس نجوم كامل على جوجل، و750 مليون درهم من الصفقات — كل صفقة يتابعها شخصياً من البداية إلى النهاية." },
      ],
    },
    properties: { index: "٠٢", overline: "المحفظة", title: "عقارات مميزة على المخطط", viewDetails: "عرض التفاصيل", handover: "التسليم", payment: "خطة الدفع", visa: "مؤهل للإقامة الذهبية", offplan: "على المخطط", filters: { all: "الكل", sobha: "شوبا", damac: "داماك", binghatti: "بنغاطي", omniyat: "أمنيات" } },
    reviews: { index: "٠٣", overline: "آراء العملاء", title: "ماذا يقول العملاء", googleLeft: "تقييم", googleRight: "على مراجعات جوجل" },
    gallery: { index: "٠٤", overline: "المعرض", title: "حياة في العقارات الفاخرة" },
    video: {
      index: "٠٤",
      overline: "السلسلة",
      title: "ذا سايلز درايف",
      kicker: "فرص استثمارية · دبي وما بعدها",
      desc: "أول سلسلة عقارية من نوعها تُطلق في دبي — حوارات صريحة بلا فلاتر مع كبار المطورين وراء أكثر مشاريع المدينة طموحاً، يقدّمها شاربل مبارك.",
      play: "شغّل الحلقة",
      allEps: "جميع الحلقات — ذا سايلز درايف",
      tagline: "عقارات دبي، بلا فلاتر.",
      subscribe: "اشترك على يوتيوب",
    },
    ig: { overline: "إنستغرام", title: "أحدث منشورات إنستغرام" },
    project: { back: "العودة إلى المحفظة", highlights: "أبرز المزايا", from: "نطاق السعر", notFound: "المشروع غير موجود", enquire: "استفسر عبر واتساب", book: "احجز اجتماعاً", type: "النوع" },
    faq: {
      index: "٠٥",
      overline: "الأسئلة الشائعة",
      title: "الشراء في دبي، بإجابات واضحة",
      items: [
        {
          q: "هل يمكن للأجانب شراء عقار في دبي؟",
          a: "نعم. يمكن للأجانب التملك بنسبة 100% في مناطق التملك الحر — مثل داون تاون دبي ودبي مارينا ونخلة جميرا والخليج التجاري. شاربل يدير العملية كاملة، عن بُعد أو شخصياً.",
        },
        {
          q: "هل شراء عقار على المخطط في دبي آمن؟",
          a: "دبي تنظّم مبيعات المخطط عبر حسابات ضمان معتمدة من ريرا — دفعاتك مرتبطة بمراحل الإنشاء وليست في جيب المطوّر. شاربل يتعامل فقط مع مطوّرين موثوقين: إعمار وداماك وشوبا وبنغاطي وأمنيات.",
        },
        {
          q: "كم أحتاج لأبدأ الاستثمار العقاري في دبي؟",
          a: "تبدأ الأسعار من نحو مليون درهم، مع خطط دفع تبدأ من 10% دفعة أولى — مثل خطة 75/25 في داماك آيلاندز. وتتوفر خطط مرنة مرتبطة بمراحل الإنشاء عبر المحفظة.",
        },
        {
          q: "هل شراء عقار في دبي يؤهلني للإقامة الذهبية؟",
          a: "نعم — الاستثمار العقاري بقيمة مليوني درهم أو أكثر يؤهلك للإقامة الذهبية لمدة 10 سنوات. كل مشاريع محفظة شاربل مؤهلة، وهو يرشد عملاءه خلال الطلب.",
        },
        {
          q: "لماذا أشتري على المخطط مع شاربل مبارك؟",
          a: "8 سنوات خبرة دولية، أكثر من 200 عميل، صفقات تتجاوز 750 مليون درهم، وتقييم 5 نجوم على جوجل — مع وصول يوم الإطلاق وخطط دفع مخصصة لعملائه في دبي وتركيا ولبنان والصين.",
        },
      ],
    },
    contact: {
      index: "٠٦",
      overline: "تواصل",
      title: "لنبدأ الحوار",
      promise: "كل استفسار يجيب عليه شاربل شخصياً — لا مساعدون، ولا مراكز اتصال.",
      methods: [
        { title: "واتساب", note: "رد سريع، عادةً خلال الساعة", cta: "راسلني على واتساب" },
        { title: "اتصل مباشرة", note: "+971 54 771 4890", cta: "اتصل الآن" },
        { title: "استشارة خاصة", note: "مراجعة محفظة لمدة 30 دقيقة، عن بُعد أو في دبي", cta: "احجز اجتماعاً" },
        { title: "إنستغرام", note: "@moubarak.charbel", cta: "تابع آخر الصفقات" },
      ],
      form: {
        name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        phone: "رقم الهاتف",
        interest: "نوع العقار",
        interests: ["تجاري", "سكني", "أخرى"],
        message: "حدثني عن أهدافك الاستثمارية…",
        submit: "أرسل الاستفسار",
        sending: "جارٍ الإرسال…",
      },
      errors: {
        name: "يرجى إدخال الاسم الكامل.",
        email: "يرجى إدخال بريد إلكتروني صحيح.",
        phone: "يرجى إدخال رقم الهاتف.",
        interest: "يرجى اختيار نوع العقار.",
        message: "يرجى كتابة رسالة قصيرة.",
      },
      success: "تم إرسال استفسارك. سيتواصل معك شاربل خلال 24 ساعة.",
      failure: "حدث خطأ ما — يرجى التواصل عبر واتساب للرد الفوري.",
    },
    footer: { tagline: "دبي | أسواق دولية | أخصائي العقارات الفاخرة على المخطط", rights: "جميع الحقوق محفوظة." },
  },
};

const LangCtx = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  return <LangCtx.Provider value={{ lang, setLang, t: STRINGS[lang] }}>{children}</LangCtx.Provider>;
};

export const useLanguage = () => useContext(LangCtx);
;
